/**
 * --- KONSEP: PENGAWAS STOK (STOCK NOTIFIER) ---
 * Komponen ini bekerja "di balik layar" (tidak terlihat di layar).
 * Tugasnya adalah mengecek stok barang secara berkala dan memunculkan
 * peringatan jika ada barang yang hampir habis.
 */
import { useEffect, useCallback } from "react";
import { productService } from "../../services/productService";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";

const StockNotifier = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  // KUNCI PENYIMPANAN: Nama file di dalam memori Browser (Local Storage)
  const STORAGE_KEY = "harapan_kita_notified_stock_ids";

  // LOGIKA: Mengambil daftar ID yang sudah diberitahu dari memori Browser
  const getNotifiedIds = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      console.error("Gagal mengambil memori notifikasi:", e);
      return new Set();
    }
  }, []);

  // LOGIKA: Menyimpan daftar ID ke dalam memori Browser
  const saveNotifiedIds = useCallback((ids) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
    } catch (e) {
      console.error("Gagal menyimpan memori notifikasi:", e);
    }
  }, []);

  // Meminta izin ke Browser untuk menampilkan notifikasi Desktop saat pertama kali buka
  useEffect(() => {
    if (!("Notification" in window)) {
      return;
    }

    if (
      Notification.permission !== "granted" &&
      Notification.permission !== "denied"
    ) {
      Notification.requestPermission();
    }
  }, []);

  /**
   * --- LOGIKA: Mengirim Pesan Peringatan ---
   */
  const sendNotification = useCallback((product) => {
    // 1. Tampilkan notifikasi di dalam aplikasi (Popup)
    showNotification({
      type: "warning",
      title: "Peringatan Stok Tipis",
      message: `${product.name} (SKU: ${product.sku}) tersisa ${product.stock_level} unit. Harap restock.`,
    });

    // 2. Tampilkan notifikasi Desktop (jika diizinkan)
    if (Notification.permission === "granted") {
      new Notification("Peringatan Inventaris", {
        body: `${product.name} (SKU: ${product.sku}) sisa ${product.stock_level} unit. Segera isi ulang stok!`,
        icon: "/favicon.ico",
        tag: `stock-alert-${product.id}`,
      });
    }
  }, [showNotification]);

  /**
   * --- LOGIKA: Mengecek Gudang (Inventory) ---
   */
  const checkInventory = useCallback(async () => {
    if (!user) return; // Hanya cek stok jika sudah login

    try {
      const products = await productService.getProductsForAlerts();
      const currentNotified = getNotifiedIds();
      let hasChanges = false;

      products.forEach((product) => {
        const isLow = product.stock_level <= product.min_stock_level;

        // Jika stok tipis dan belum pernah diingatkan sebelumnya
        if (isLow && !currentNotified.has(product.id)) {
          sendNotification(product);
          currentNotified.add(product.id);
          hasChanges = true;
        } else if (!isLow && currentNotified.has(product.id)) {
          // Jika stok sudah diisi lagi, hapus dari daftar pengingat
          currentNotified.delete(product.id);
          hasChanges = true;
        }
      });

      // Jika ada perubahan pada daftar (ada yang baru diingatkan atau sudah diisi), simpan!
      if (hasChanges) {
        saveNotifiedIds(currentNotified);
      }
    } catch (err) {
      console.error("Kesalahan saat mengecek stok:", err);
    }
  }, [user, sendNotification, getNotifiedIds, saveNotifiedIds]);

  // Menjalankan pengecekan setiap 2 menit (120000 milidetik)
  useEffect(() => {
    if (user) {
      checkInventory(); // Cek langsung saat login
      const interval = setInterval(checkInventory, 120000);
      return () => clearInterval(interval);
    }
  }, [user, checkInventory]);

  return null; // Komponen ini tidak menggambar apa pun di layar
};

export default StockNotifier;
